import 'react-quill/dist/quill.bubble.css';
import '../styles/InlineQuillEdit.css';

import { Delta, RangeStatic, Sources } from 'quill';
import React, {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { isArray, isEmpty, isFunction, mergeWith, uniq } from 'lodash';

import { ASSET_TYPE_VALUES } from '../../shared/models/Asset';
import AlertDialog from '../../shared/components/AlertDialog';
import { Alignment } from '../models/LessonComponentSettings';
import AssetLibrary from '../../shared/components/AssetLibrary/AssetLibrary';
import AssetService from '../../shared/services/AssetService';
import Button from '@mui/material/Button';
import ColorPickerDialog from '../../shared/components/ColorPickerDialog';
import ColorPickerService from '../../shared/services/ColorPickerService';
import Constants from '../constants';
import LessonComponentService from '../services/LessonComponentService';
import { LessonComponentStyleType } from '../models/LessonComponentStyleType';
import { RgbaColor } from '../models/LessonComponentStyle';
import { nanoid } from 'nanoid';
import sanitizeHtml from 'sanitize-html';
import { useLessonComponentContext } from '../models/LessonComponentContext';
import { useLessonContext } from '../models/LessonContext';

export interface InlineQuillEditProps {
  value: string;
  styleType: LessonComponentStyleType;
  stylePosition?: Alignment;
  componentKey: number;
  disable?: boolean; //used only in the freetext answer
  placeholder: string;
  isCenterAligned?: boolean;
  notAllowEmptyValue?: boolean;
  /** Handle enter key as submission for 'label' type components
   * (Single selection , multiple selection and slider)
   */
  restrictEnterKey?: boolean;
  onCharactersCountChange?: (count: number) => void;
  onChange: (value: string) => void;
  onClick?: (value: string, id: string) => void;
}

const Embed = Quill.import('blots/embed');
const InlineBlot = Quill.import('blots/inline');

class FillInBlank extends Embed {
  static create(value: any) {
    let node: HTMLElement = super.create(value);
    node.setAttribute('id', value.id);
    node.setAttribute('data-index', `${value.index}`);
    node.setAttribute('component-type', `${value.componentType}`);
    node.textContent = `${
      value.componentType === 'fill_menu' ? 'Selector' : 'Blank'
    } ${value.index}`;
    return node;
  }
  static value(node: HTMLElement): any {
    return {
      id: node.getAttribute('id'),
      index: +node.getAttribute('data-index'),
      componentType: node.getAttribute('component-type')
    };
  }
}

FillInBlank.blotName = 'blank';
FillInBlank.className = 'fill-the-blank';
FillInBlank.tagName = 'blank';

Quill.register(FillInBlank);

class MediaLink extends InlineBlot {
  static create(value: any) {
    let node = super.create();
    node.setAttribute('href', value.url);
    node.setAttribute('target', '_blank');
    node.setAttribute('data-assetkey', value.assetkey);

    return node;
  }

  static formats(node: HTMLElement) {
    return {
      url: node.getAttribute('href'),
      assetkey: node.getAttribute('data-assetkey')
    };
  }
}

MediaLink.blotName = 'mediaLink';
MediaLink.className = 'media-link';
MediaLink.tagName = 'a';

Quill.register(MediaLink);

class NewLine extends Embed {
  static create(value: any) {
    let node: HTMLElement = super.create(value);
    node.innerHTML = '<span style="visibility: hidden">&#x23ce;</span><br/>';
    return node;
  }
}
NewLine.blotName = 'newline';
NewLine.className = 'newline';
NewLine.tagName = 'newline';
Quill.register(NewLine);

interface InlineAttributes {
  [index: string]: string;
}

export function InlineQuillEdit(props: InlineQuillEditProps) {
  const { quillEditing, componentType, onBeforeClick } =
    useLessonComponentContext();
  const [editing, setEditing] = useState(false);
  const [colorPicker, setColorPicker] = useState(false);
  const [color, setColor] = useState('#000000');
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [assetID, setAssetID] = useState(null);
  const [keyDown, setKeyDown] = useState(false);
  const inputRef = useRef(null);
  const messageRef = useRef('');
  const selectionRef = useRef(0);
  const [pasted, setPasted] = useState(false);
  const prevContentRef = useRef(null);
  const { mode } = useLessonContext();
  const [showWarning, setShowWarning] = useState(false);
  const editor = document.getElementById('quill_toolbar_editor');
  const player = document.getElementById('quill_toolbar_player');

  // When there is media-link, update url in 'props.value' with the new sas token
  const contentText = LessonComponentService.getUrlWithSas(props.value || '');

  const [value, setValue] = useState(contentText ? contentText : '');

  /* Removes linespace(<p><br></p>) at the end of html content */
  const filteredContent = LessonComponentService.getFilteredContent(value);
  const maxLength = Constants.MaximumFreeTextCharacters;
  const maxFormatLength = Constants.MaxFormattingFreeTextCharacters;
  const quill = inputRef.current?.getEditor();

  useEffect(() => {
    if (value !== contentText) {
      setValue(contentText ?? '');
    }
  }, [props.value]);

  useEffect(() => {
    setEditing(quillEditing);
    if (quillEditing) {
      inputRef.current?.focus();
      const quillContent: string = quill?.getText(); // This excludes HTML tags, but includes `\n`
      const newLines = newLineCount(quillContent);

      if (quillContent?.length - newLines > maxLength) {
        const start = maxLength + newLines - 1; // By default `\n` count is 1 for the 1st line itself.
        // So it is required to deduct `1` from the `newLines` count.
        const end = quillContent.length;
        // quill.deleteText(start, end) accepts `start` and `end` params.
        inputRef.current?.editor.deleteText(start, end);
        messageRef.current = `Some content was trimmed from the end as the existing content exceeded the ${
          value?.length > maxFormatLength ? 'Formatting' : ''
        } limit.`;
        setShowWarning(true);
      }
    }
  }, [quillEditing]);

  /** props.onChange() needs to be called only on the first keypress change in the editor value.
   * If there is no value in the editor (ie, after deleting all values) then props.onChange()
   * should be called with 'null'. Basically the props.onChange() sets enable / disable the
   * 'SAVE AND CONTINUE' button in 'LessonPlayer' and 'SAVE LESSON' in 'LESSONEDITOR'.
   */
  useEffect(() => {
    if (editing && filteredContent && keyDown) {
      setKeyDown(false);
      props.onChange(filteredContent);
    }

    if (!filteredContent && (mode === 'preview' || mode === 'learn')) {
      setKeyDown(true);
      props.onChange(null);
      if (isFunction(props.onCharactersCountChange)) {
        props.onCharactersCountChange(0);
      }
    }
  }, [filteredContent, editing]);

  /* blur event listener added for the quill editor and it runs whenever the component is rendered 
  without dependencey array. This is because, when attaching 'onBlur' to ReactQuill component, blur 
  doesn't fire always specifically when clicking the button element after typing in the editor */
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current?.editor.root.addEventListener('blur', handleBlur);
      /* Catching the mousedown on toolbar container and calling e.preventDefault() to fix the 
      toolbar to be visible after clicking the toolbar options specifically after changing the color.
      Reference: https://github.com/quilljs/quill/issues/1290
      */
      inputRef.current?.editor
        .getModule('toolbar')
        .container.addEventListener('mousedown', handleMouseDown);
      return () => {
        inputRef.current?.editor
          .getModule('toolbar')
          .container.removeEventListener('mousedown', handleMouseDown);
        inputRef.current?.editor.root.removeEventListener('blur', handleBlur);
      };
    }
  });

  useEffect(() => {
    if (isFunction(props.onCharactersCountChange) && quill) {
      quill.on('selection-change', handleSelectionChange);
      quill.root.addEventListener('paste', handlePaste);

      return () => {
        quill.off('selection-change', handleSelectionChange);
        quill.root.removeEventListener('paste', handlePaste);
      };
    }
  });

  const handleSelectionChange = (range: RangeStatic | null) => {
    const quillContent: string = quill.getText(); // This excludes HTML tags
    const newLines = newLineCount(
      quillContent.slice(range?.index, range?.index + range?.length)
    );
    // It is important to deduct new lines from the selected text.
    selectionRef.current = range?.length - newLines || 0;
  };

  const handlePaste = (e: ClipboardEvent) => {
    setPasted(true);
    const raw = e.clipboardData?.getData('text/plain') || '';
    // Replace all CRLFs and normalize
    const pastedText = raw.replace(/\r\n/g, '\n').replace(/\n\n+/g, '\n'); // removes extra blank lines
    const textContent = inputRef.current?.editor.root.textContent;
    const editorLength = textContent?.length;
    const pastingNewLines = newLineCount(pastedText);
    const pastedLength = pastedText.length - pastingNewLines;
    const actualLengthAfterPaste =
      editorLength - selectionRef.current + pastedLength;
    // If pasted length exceeds the max allowed limit, then trim exceess content
    // OR
    // do not paste the copied content
    if (actualLengthAfterPaste > maxLength) {
      if (editorLength === 0 || selectionRef.current === editorLength) {
        messageRef.current =
          'Some content was trimmed from the end as the copied content exceeded the limit.';
      } else {
        messageRef.current = 'Paste failed: copied content exceeds the limit.';
        e.preventDefault();
        setShowWarning(true);
      }
    }
  };

  //Define toolbar text formatting used in different components
  const IU = ['italic', 'underline'];
  const BIU = ['bold', ...IU];
  const SCRIPT = [{ script: 'sub' }, { script: 'super' }];
  const ALIGN = [{ align: '' }, { align: 'center' }, { align: 'right' }];
  const LIST = [{ list: 'ordered' }, { list: 'bullet' }];
  const EXTRA = ['customColor', 'link', 'customLink'];

  const base = [SCRIPT, ALIGN, EXTRA];

  const getContainerFormats = () => {
    switch (props.styleType) {
      case 'h1':
      case 'h2':
        return [IU, ...base];
      case 'p':
        return [BIU, LIST, ...base];
      case 'label':
        return [BIU, SCRIPT, ['customColor']];
      case 'noList':
        return [BIU, ...base];
      case 'noLink':
        return [BIU, SCRIPT, ALIGN, LIST, ['customColor']];
      case 'noLinkList':
        return [BIU, SCRIPT, ALIGN, ['customColor']];
      case 'noAlignList':
        return [BIU, SCRIPT, EXTRA];
      default:
        return null;
    }
  };

  const defaultModules = useMemo(
    () => ({
      toolbar: {
        container: getContainerFormats(),
        handlers: {
          customColor: function () {
            setColorPicker(true);
          },
          customLink: function () {
            //Get the assetkey from selected text if there is any link added and pass it in AssetLibrary.
            //If the selected text doesn't have any link then assetkey will be null.
            const range = inputRef.current.editor?.getSelection();
            const format = inputRef.current.editor?.getFormat(range);
            if (!isEmpty(format) && format.mediaLink) {
              setAssetID(format.mediaLink.assetkey);
            }
            setShowAssetLibrary(true);
          }
        }
      },
      clipboard: {
        matchVisual: false
      }
    }),
    []
  );

  const modules = useMemo(
    () =>
      props.restrictEnterKey
        ? {
            ...defaultModules,
            keyboard: {
              bindings: {
                tab: false,
                noEnterKey: {
                  key: 13,
                  shiftKey: null,
                  altKey: null,
                  metaKey: null,
                  ctrlKey: null,
                  handler: function () {
                    // restrict enter key
                  }
                }
              }
            }
          }
        : {
            ...defaultModules,
            keyboard: {
              bindings: {
                shift_enter: {
                  key: 13,
                  shiftKey: true,
                  handler: (range: any, context: any) => {
                    if (context?.format?.list) {
                      inputRef.current.editor.insertEmbed(
                        range.index,
                        'newline',
                        {}
                      );
                      setEditorIndex();
                      setValue(inputRef.current.editor.root.innerHTML);
                    } else {
                      // Return TRUE to use default handler.
                      return true;
                    }
                  }
                }
              }
            }
          },
    [props.restrictEnterKey]
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'script',
    'align',
    'list',
    'bullet',
    'indent',
    'link',
    'color',
    'blank',
    'newline',
    'mediaLink'
  ];

  const handleClose = (rgba: RgbaColor) => {
    if (rgba) {
      const color = ColorPickerService.rgbToHex(rgba);
      inputRef.current.editor.format('color', color);
      setColor(color);
    }
    setColorPicker(false);
  };

  const options = {
    h1: ['class'],
    h2: ['class'],
    p: ['class'],
    li: ['class'],
    a: ['style', 'rel', 'data-assetkey', 'class', 'href'],
    em: ['style'],
    sup: ['style'],
    sub: ['style'],
    span: ['style'],
    strong: ['style'],
    u: ['style'],
    blank: ['class', 'id', 'data-index', 'component-type'],
    newline: [] as string[]
  };

  const customizer = (
    options: InlineAttributes,
    sanitizeOptions: InlineAttributes
  ) => {
    if (isArray(options)) {
      return uniq(options.concat(sanitizeOptions));
    }
  };

  const mergedAttributes = mergeWith(
    options,
    sanitizeHtml.defaults.allowedAttributes,
    customizer
  );

  const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    if (onBeforeClick) {
      onBeforeClick(props.componentKey);
      setEditing(true);
    }
  };

  const setEditorIndex = () => {
    setTimeout(
      () =>
        inputRef.current.editor.setSelection(
          inputRef.current.editor.getSelection().index + 1,
          0
        ),
      0
    );
  };

  const handleMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const sanitizedValue = (value: string) => {
    return sanitizeHtml(value, {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat([
        'blank',
        'newline'
      ]),
      allowedAttributes: mergedAttributes
    });
  };

  /** Count of `\n` presents in the given quill content.  */
  const newLineCount = (textContent: string): number => {
    if (!textContent) {
      return 0;
    }
    const count = [...textContent].filter((c) =>
      ['\n', '\r'].includes(c)
    )?.length;
    return count;
  };

  const handleChange = (content: string, delta: Delta, source: Sources) => {
    if (!pasted && content.length > maxFormatLength && componentType === 'free_text') {
      messageRef.current = `Reached maximum limit.`;
      setShowWarning(true);
    } else {
      if (messageRef.current && showWarning) {
        // Restrict pasting changes and retain previously selection text
        quill.setContents(prevContentRef.current);
      } else {
        //When blank is inserted, this shouldn't execute
        const hasBlankInsert = delta.filter((d) => d.insert && d.insert.blank);
        if (!hasBlankInsert.length) {
          if (!filteredContent) {
            /* Sets the placeholder text after the quill editor is instantiated. so that it will display 
        when all the content gets deleted */
            if (inputRef.current) {
              inputRef.current.editor.root.dataset.placeholder =
                props.placeholder;
            }
          }

          /* set the content in state to allow the new line (eg. list options) */
          setValue(content);
          // Support for showing the characters count only for free text component
          if (isFunction(props.onCharactersCountChange) && inputRef.current && componentType === 'free_text') {
            const textContent = inputRef.current?.editor.root.textContent;
            const editorLength = textContent?.length; // Visible text content length
            const quillContent: string = quill.getText(); // This excludes HTML tags, but includes `\n`
            const newLines = newLineCount(quillContent);
            if (!pasted && content.length > maxFormatLength) {
              // Revert the last change
              quill.updateContents(
                delta.ops.map((op: any) => {
                  if (op.attributes) {
                    return {
                      ...op,
                      attributes: {} // remove formatting
                    };
                  }
                  return op;
                }),
                'silent'
              );

              // Set the editor content back to previous valid content
              quill.setContents(prevContentRef.current);
              messageRef.current = `Formatting could not be applied as it exceeds the allowed limit.`;
              setShowWarning(true);
            } else {
              // Save current state as previous valid content
              prevContentRef.current = quill.getContents();
              setShowWarning(false);
            }

            // Handle Paste event change
            if (pasted) {
              setPasted(false);
              if (editorLength > maxLength) {
                const start = maxLength + newLines - 1; // By default `\n` count is 1 for the 1st line itself.
                // So it is required to deduct `1` from the `newLines` count.
                const end = editorLength;
                // quill.deleteText(start, end) accepts `start` and `end` params.
                inputRef.current?.editor.deleteText(start, end);
                setShowWarning(true);
              }
            }
            // Sometimes the `deleteText()` is not getting updated with the content count.
            // So it needs to be re-calculated again.
            const currentLength =
              inputRef.current?.editor.root.textContent?.length;
            props.onCharactersCountChange(
              textContent === '' ? 0 : currentLength
            );
          }
        }
      }
    }
    selectionRef.current = 0;
  };

  /** Sanitize the html content before saving into db */
  const handleSubmit = (content: string) => {
    // Retain the previous value when there is no content.
    if (props.notAllowEmptyValue && !content) {
      setValue(contentText);
      props.onChange(sanitizedValue(contentText));
    } else {
      props.onChange(sanitizedValue(content));
    }
  };

  /* props.onChange called in the onBlur to avoid slowness 
  (https://thetarngroup.atlassian.net/browse/B2-1238)  */
  const handleBlur = () => {
    /** The Quill editor 'mouseDown' property returns true when clicking outside of the editor.  
          When copy/paste content into quill editor, the 'mouseDown' value is false so props.onChange 
          won't be called.
      */
    const actualBlur = inputRef.current?.editor.selection.mouseDown;
    if (actualBlur) {
      /** Passing 'content' instead of 'filteredContent' to allow new line when the content
           has list (ordered/un-ordered) added. Passing empty '' when filteredContent is empty.
         */
      const editContent = !filteredContent ? '' : value;
      handleSubmit(editContent);
    }
  };

  const handleFocus = () => {
    inputRef.current?.focus();
    setEditing(true);
  };

  const handleBlankClick = () => {
    const dom = new DOMParser().parseFromString(value, 'text/html');
    const blankIndex = dom.querySelectorAll('.fill-the-blank').length + 1;
    let range = inputRef.current.editor.getSelection();

    const id = nanoid();
    if (range) {
      inputRef.current.editor.insertEmbed(range.index, 'blank', {
        id: id,
        index: blankIndex, // count of existing blanks. Should be taken from component content
        componentType
      });
      setEditorIndex();
      setValue(inputRef.current.editor.root.innerHTML);

      props.onClick(inputRef.current.editor.root.innerHTML, id);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (isFunction(props.onCharactersCountChange)) {
      const quillLength = quill.getLength();
      const quillContent: string = quill.getText(); // This excludes HTML tags
      const newLines = newLineCount(quillContent);
      const exemptionKeys =
        ['a', 'c', 'v'].includes(event.key.toLocaleLowerCase()) && // Allow select All, Copy and Paste
        (event.ctrlKey || event.metaKey); // ctrlKey for Windows, metaKey for Mac
      if (
        quillLength - newLines >= maxLength &&
        event.key !== 'Backspace' &&
        event.key !== 'Delete' &&
        !exemptionKeys
      ) {
        event.preventDefault();
      }
    }
    if (props.restrictEnterKey && ['Enter', 'Tab'].includes(event.key)) {
      setEditing(false);
      handleSubmit(filteredContent);
    }
  };

  //handle onSelect of custom link to attach the selected file
  const handleCustomLinkSelect = (assetkey: string) => {
    //Focuses the editor as sometimes editor losing focus when clicking custom link button
    inputRef.current?.focus();

    //Find the selected text range and use formatText to add the media link.
    const range = inputRef.current.editor?.getSelection();
    const url = AssetService.getAssetUrl(assetkey);
    inputRef.current.editor.formatText(range.index, range.length, 'mediaLink', {
      url,
      assetkey
    });
  };

  // Dynamically set the align-content variable used in Text/Image Combo component
  const customStyle: React.CSSProperties = {
    '--align-content': props.stylePosition
  };

  return (
    <>
      <span onClick={handleClick} className={'ql-editor'}>
        {!editing ? (
          <span
            /* This has been used in Text/Image Combo component  */
            style={props.stylePosition && { alignContent: props.stylePosition }}
            className={`noSelect ${
              filteredContent
                ? ''
                : `placeholder-color ${
                    props.isCenterAligned ? 'placeholder-align' : ''
                  }`
            }`}
            dangerouslySetInnerHTML={{
              // filteredContent is the content without the line space ie. <p><br></p>
              // Displaying original value(including line spaces at end) here not the filteredContent to
              // avoid flickering.
              // Line spaces at the end of content will be removed before storing into db.
              __html: filteredContent || props.placeholder
            }} //value has been sanitized before storing into db.
          ></span>
        ) : (
          <>
            <ReactQuill
              ref={inputRef}
              theme="bubble"
              bounds={editor ? editor : player}
              value={value}
              onChange={handleChange}
              modules={modules}
              formats={formats}
              placeholder={props.placeholder}
              onFocus={handleFocus}
              readOnly={props.disable}
              onKeyDown={handleKeyDown}
              /* This has been used in Text/Image Combo component  */
              style={props.stylePosition && customStyle}
            />
            {colorPicker && (
              <ColorPickerDialog
                color={ColorPickerService.hexToRgbA(color)}
                onClose={handleClose}
              />
            )}
          </>
        )}
        {['fill_blank', 'fill_menu'].includes(componentType) &&
          props.styleType === 'noLink' && (
            <div style={{ marginTop: '20px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBlankClick}
                disabled={!editing && props.styleType === 'noLink'}
              >
                {`Add ${componentType === 'fill_blank' ? 'blank' : 'selector'}`}
              </Button>
            </div>
          )}
      </span>
      {showAssetLibrary && (
        <AssetLibrary
          types={[...ASSET_TYPE_VALUES]}
          category="author"
          title={'Select a file'}
          selectedAssetkey={assetID}
          onSelect={handleCustomLinkSelect}
          onClose={() => {
            setAssetID(null);
            setShowAssetLibrary(false);
          }}
        />
      )}
      {isFunction(props.onCharactersCountChange) &&
        showWarning &&
        messageRef.current && (
          <AlertDialog
            title="Warning"
            message={messageRef.current}
            onClose={() => {
              setShowWarning(false);
              messageRef.current = '';
            }}
          />
        )}
    </>
  );
}
